import { useEffect, useMemo, useState } from 'react'
import { usePageData } from '@/hooks/usePageData'
import type { GlossaryCategory, GlossaryEntry, GlossaryPageData } from '@/types/glossary'

/*
  Glossary page — June 2026 redesign (client-supplied desktop/mobile mocks).
  - Terms grouped into category sections (Glossary Category database in IIA;
    each entry's `category` stores the category record id).
  - Sections ordered alphabetically by category name; entries whose category
    is blank, unknown, or inactive fall into a "General" section at the end,
    so nothing disappears while categories are being populated.
  - Each section: Alfarn 2 heading over a thick black rule, then accordion
    rows — term (Poppins Bold) left, +/− toggle right, thin rule per row.
  - Rows collapse by default; multiple rows can be open at once. Expanded
    rows show the description HTML (and the optional image below it).
  - BACK button above the footer, mirroring the artist-detail pattern.

  Styling lives in src/styles/pages/glossary.css.
*/

interface CategorySection {
  name: string
  entries: GlossaryEntry[]
}

/* ---- Group entries into category sections ----
   Active categories only, ordered A-Z by name (the GraphQL query already
   sorts them); "General" collects everything else and renders last. */
function groupByCategory(
  entries: GlossaryEntry[],
  categories: GlossaryCategory[],
): CategorySection[] {
  const activeById = new Map<string, GlossaryCategory>()
  for (const c of categories) {
    if (c.active === true && (c.category_name ?? '').trim()) {
      activeById.set(String(c.id), c)
    }
  }

  const byCategory = new Map<string, GlossaryEntry[]>()
  const general: GlossaryEntry[] = []
  for (const entry of entries) {
    const key = String(entry.category ?? '')
    if (key && activeById.has(key)) {
      const list = byCategory.get(key) ?? []
      list.push(entry)
      byCategory.set(key, list)
    } else {
      general.push(entry)
    }
  }

  const sections: CategorySection[] = []
  // `categories` arrives sorted by category_name ASC; keep that order.
  for (const c of categories) {
    const list = byCategory.get(String(c.id))
    if (list?.length) sections.push({ name: c.category_name, entries: list })
  }
  if (general.length) sections.push({ name: 'General', entries: general })
  return sections
}

/* ---- +/− toggle icon (matches the mock's plus that loses its vertical
   bar when the row is open) ---- */
function PlusMinusIcon({ open }: { open: boolean }) {
  return (
    <span aria-hidden="true" className="mig-glossary-row-icon" data-open={open}>
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <rect x="0" y="10" width="24" height="4" />
        {!open && <rect x="10" y="0" width="4" height="24" />}
      </svg>
    </span>
  )
}

/* ---- One accordion row: term + toggle, expandable description ---- */
function GlossaryRow({
  entry,
  open,
  onToggle,
}: {
  entry: GlossaryEntry
  open: boolean
  onToggle: () => void
}) {
  const imageUrl =
    entry.image?.versions?.card ||
    entry.image?.versions?.large ||
    entry.image?.url ||
    null

  return (
    <div className="mig-glossary-item">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`glossary-panel-${entry.id}`}
        className="mig-glossary-row"
      >
        <span className="mig-glossary-row-term">{entry.term}</span>
        <PlusMinusIcon open={open} />
      </button>

      {open && (
        <div id={`glossary-panel-${entry.id}`} className="mig-glossary-panel">
          {entry.description && (
            <div
              className="mig-glossary-description"
              dangerouslySetInnerHTML={{ __html: entry.description }}
            />
          )}
          {imageUrl && (
            <div className="mig-glossary-panel-image">
              <img src={imageUrl} alt={entry.term} loading="lazy" decoding="async" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ======================================================================
   Glossary Page
   ====================================================================== */
export default function Glossary() {
  const data = usePageData<GlossaryPageData>()
  const entries = data?.entries ?? []
  const categories = data?.categories ?? []

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Multiple rows can be open at once; all collapsed on load.
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set())
  const toggle = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const sections = useMemo(() => groupByCategory(entries, categories), [entries, categories])

  // BACK mirrors the artist-detail pattern: return to the same-origin
  // referrer when there is one (the footer links here from every page),
  // otherwise fall back to home.
  const backHref = (() => {
    try {
      const ref = document.referrer
      if (ref) {
        const url = new URL(ref)
        if (url.origin === window.location.origin && url.pathname !== '/glossary') {
          return url.pathname
        }
      }
    } catch { /* ignore */ }
    return '/'
  })()

  return (
    <section className="mig-page-section">
      {/* Page heading - h1-title page (180px) */}
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-glossary-page-title">Glossary</h1>
      </div>

      <div className="mig-glossary-content">
        {entries.length === 0 ? (
          <p className="mig-glossary-empty">No glossary terms yet. Check back soon.</p>
        ) : (
          sections.map((section) => (
            <section key={section.name} className="mig-glossary-section">
              <h2 className="mig-glossary-section-heading">{section.name}</h2>
              <div className="mig-glossary-accordion">
                {section.entries.map((entry) => (
                  <GlossaryRow
                    key={entry.id}
                    entry={entry}
                    open={openIds.has(String(entry.id))}
                    onToggle={() => toggle(String(entry.id))}
                  />
                ))}
              </div>
            </section>
          ))
        )}

        {/* BACK button */}
        <div className="mig-page-back-row">
          <a href={backHref} className="mig-glossary-back-button">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </div>
    </section>
  )
}
